/**
 * Os quatro "Excel em lote" do menu antigo, e as sugestões de apelido.
 *
 * **A tela existe para conferir.** O sistema antigo tem só o "aplicar": manda a
 * planilha e descobre depois o que mudou. Aqui a conferência roda a planilha
 * inteira **sem gravar** e mostra linha a linha — o que está no banco hoje ao
 * lado do que a planilha traz — e só então aparece o botão de aplicar.
 *
 * **A linha que mais importa é a de "outro projeto".** O `UPDATE` do legado é
 * `WHERE componente_id = @id` e mais nada: uma planilha com um id de outro
 * projeto altera o componente de outro projeto, em silêncio, sem ninguém saber.
 * Aqui isso vira linha recusada com o motivo, contada à parte.
 *
 * **As sugestões de apelido são o caminho oposto**, e costumam ser melhores que
 * a planilha: o mesmo componente já foi batizado em outros projetos, e o nome
 * pode vir de lá em vez de ser inventado ou digitado no Excel. Em produção, 19%
 * dos componentes estão sem apelido.
 */

import { useMemo, useRef, useState } from "react"
import { useParams } from "react-router-dom"
import {
  baixarPlanilhaDeApelidos, CAMPOS_DO_LOTE, usarAplicarLote, usarAplicarApelidos, usarConferirLote, usarSugestoesDeApelido,
  type CampoDoLote, type ConferenciaDoLote, type LinhaDoLote,
} from "@/api/loteDeComponentes"
import { BarraDeEstado } from "@/kit/BarraDeEstado"
import { Aviso, Botao, Campo, estiloCampo, mensagem } from "../outros/pecas"

const MOSTRADAS = 300

/** Botão solto na barra: sobe os 9 px de margem do `Campo` para ficar na linha dos inputs. */
const NA_LINHA_DOS_CAMPOS: React.CSSProperties = { marginBottom: 9 }

export function LoteDeComponentes() {
  const { projetoId } = useParams()
  const projeto = Number(projetoId)

  const [campo, setCampo] = useState<CampoDoLote>("apelido")
  const [colunaId, setColunaId] = useState(1)
  // Apelido é o campo da planilha baixada (id | código | descrição | apelido): id na 1, valor na 4.
  const [colunaValor, setColunaValor] = useState(4)
  const [baixando, setBaixando] = useState(false)
  const [arquivo, setArquivo] = useState<File | null>(null)
  const [recado, setRecado] = useState<string | null>(null)
  const [conferencia, setConferencia] = useState<ConferenciaDoLote | null>(null)
  const [aplicado, setAplicado] = useState<number | null>(null)
  const entrada = useRef<HTMLInputElement>(null)

  const conferir = usarConferirLote()
  const aplicar = usarAplicarLote()

  const pronto = arquivo != null && colunaId > 0 && colunaValor > 0 && colunaId !== colunaValor

  function trocarCampo(novo: CampoDoLote) {
    setCampo(novo)
    setConferencia(null)
    setColunaId(1)
    setColunaValor(novo === "apelido" ? 4 : 2)
  }

  async function baixarPlanilha() {
    setBaixando(true)
    setRecado(null)
    try {
      await baixarPlanilhaDeApelidos(projeto)
    } catch (e) {
      setRecado(mensagem(e))
    } finally {
      setBaixando(false)
    }
  }

  function escolher(a: File | undefined) {
    if (!a) return
    setArquivo(a)
    // Trocar a planilha com uma conferencia na tela deixaria o botao de aplicar
    // apontando para o arquivo anterior.
    setConferencia(null)
    setAplicado(null)
    setRecado(null)
  }

  function rodarConferencia() {
    if (!arquivo) return
    setRecado(null)
    setAplicado(null)
    conferir.mutate(
      { projetoId: projeto, campo, colunaId, colunaValor, arquivo },
      { onSuccess: setConferencia, onError: (e) => { setConferencia(null); setRecado(mensagem(e)) } })
  }

  function rodarAplicacao() {
    if (!arquivo) return
    setRecado(null)
    aplicar.mutate(
      { projetoId: projeto, campo, colunaId, colunaValor, arquivo },
      {
        onSuccess: (r) => {
          setConferencia(r.conferencia)
          setAplicado(r.alterados)
          setRecado(`${r.alterados} componente(s) alterado(s).`)
        },
        onError: (e) => setRecado(mensagem(e)),
      })
  }

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <div style={{
        display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end",
        padding: "12px 18px", borderBottom: "1px solid var(--line2)",
      }}>
        <Campo rotulo="O que alterar">
          <select value={campo} onChange={(e) => trocarCampo(e.target.value as CampoDoLote)}
                  style={estiloCampo}>
            {CAMPOS_DO_LOTE.map((c) => <option key={c.chave} value={c.chave}>{c.rotulo}</option>)}
          </select>
        </Campo>

        <Campo rotulo="Coluna do id">
          <input type="number" min={1} value={colunaId}
                 onChange={(e) => { setColunaId(Number(e.target.value)); setConferencia(null) }}
                 style={{ ...estiloCampo, width: 90 }} />
        </Campo>

        <Campo rotulo="Coluna do valor">
          <input type="number" min={1} value={colunaValor}
                 onChange={(e) => { setColunaValor(Number(e.target.value)); setConferencia(null) }}
                 style={{ ...estiloCampo, width: 90 }} />
        </Campo>

        <Campo rotulo="Planilha">
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input ref={entrada} type="file" accept=".xlsx" style={{ display: "none" }}
                   onChange={(e) => escolher(e.target.files?.[0])} />
            <Botao aoClicar={() => entrada.current?.click()}>
              {arquivo ? "Trocar" : "Escolher"}
            </Botao>
            <span style={{ fontSize: 11.5, color: arquivo ? "var(--txt)" : "var(--txt3)" }}>
              {arquivo ? arquivo.name : "nenhuma"}
            </span>
          </div>
        </Campo>

        {campo === "apelido" && (
          <div style={NA_LINHA_DOS_CAMPOS}>
            <Botao aoClicar={baixarPlanilha} ocupado={baixando}
                   titulo="Baixa o .xlsx com id, código, descrição e apelido de cada componente do projeto.">
              {baixando ? "Baixando…" : "Baixar planilha de apelidos"}
            </Botao>
          </div>
        )}

        <div style={NA_LINHA_DOS_CAMPOS}>
          <Botao destaque aoClicar={rodarConferencia} desligado={!pronto} ocupado={conferir.isPending}>
            {conferir.isPending ? "Conferindo…" : "Conferir"}
          </Botao>
        </div>

        {conferencia && conferencia.vaoMudar > 0 && aplicado == null && (
          <div style={NA_LINHA_DOS_CAMPOS}>
            <Botao aoClicar={rodarAplicacao} ocupado={aplicar.isPending}
                   titulo="Só as linhas marcadas como “vai mudar”.">
              {aplicar.isPending
                ? "Aplicando…"
                : `Aplicar ${conferencia.vaoMudar} linha(s)`}
            </Botao>
          </div>
        )}
      </div>

      {/* A ajuda fica numa linha só, fora dos Campos: ajuda dentro de um Campo muda a altura
          dele e a barra (alinhada pela base) virava escada. */}
      <p style={{ margin: 0, padding: "6px 18px 0", fontSize: 10.5, color: "var(--txt3)" }}>
        Colunas contadas a partir de 1, como o Excel mostra; a linha 1 é cabeçalho e é pulada.
        {campo === "apelido" &&
          " Na planilha de apelidos, preencha a coluna apelido e envie o mesmo arquivo, sem mexer nas colunas."}
      </p>

      {colunaId === colunaValor && (
        <div style={{ padding: "8px 18px 0" }}>
          <Aviso>As duas colunas são a mesma ({colunaId}). A API recusa — e teria de recusar.</Aviso>
        </div>
      )}

      {recado && <div style={{ padding: "8px 18px 0" }}><Aviso>{recado}</Aviso></div>}

      <div style={{ flex: 1, minHeight: 0, overflow: "auto", padding: 14 }}>
        {!conferencia ? (
          <p style={{ fontSize: 12.5, color: "var(--txt3)", maxWidth: "64ch", lineHeight: 1.6 }}>
            Escolha o campo, diga em que coluna está o id do componente e em que coluna está o
            valor novo, e mande conferir. <b>Nada é gravado na conferência</b> — ela roda a
            planilha inteira e mostra o que aconteceria, linha a linha, com o valor de hoje ao
            lado. O sistema antigo não tem esse passo: manda a planilha e descobre depois.
          </p>
        ) : (
          <Relatorio c={conferencia} aplicado={aplicado} />
        )}
      </div>

      <BarraDeEstado
        direita={conferencia ? `${conferencia.linhasNaPlanilha} linha(s) na planilha` : undefined}
      >
        {conferencia
          ? `${conferencia.vaoMudar} vão mudar · ${conferencia.foraDoProjeto} de outro projeto · ${conferencia.naoExistem} não existem`
          : "A conferência não grava nada."}
      </BarraDeEstado>
    </div>
  )
}

function Relatorio({ c, aplicado }: { c: ConferenciaDoLote; aplicado: number | null }) {
  const [soAsQueMudam, setSoAsQueMudam] = useState(false)

  const linhas = useMemo(
    () => (soAsQueMudam ? c.linhas.filter((l) => /vai mudar/i.test(l.situacao)) : c.linhas),
    [c.linhas, soAsQueMudam])

  const mostradas = linhas.slice(0, MOSTRADAS)

  return (
    <>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
        <Numero rotulo="Vão mudar" valor={c.vaoMudar} cor={c.vaoMudar > 0 ? "var(--ok)" : undefined} />
        <Numero rotulo="Já estão assim" valor={c.jaEstaoAssim} />
        <Numero rotulo="De outro projeto" valor={c.foraDoProjeto}
                cor={c.foraDoProjeto > 0 ? "var(--erro)" : undefined} />
        <Numero rotulo="Não existem" valor={c.naoExistem} cor={c.naoExistem > 0 ? "var(--erro)" : undefined} />
        <Numero rotulo="Linha incompleta" valor={c.semDados} />
      </div>

      {c.foraDoProjeto > 0 && (
        <Aviso>
          <b>{c.foraDoProjeto} linha(s) apontam para componente de outro projeto</b> e ficaram de
          fora. No sistema antigo elas teriam sido alteradas em silêncio — o <code>UPDATE</code> de
          lá não confere o projeto.
        </Aviso>
      )}

      {aplicado != null && (
        <p style={{ margin: "0 0 10px", fontSize: 12.5 }}>
          <b>{aplicado} componente(s) alterado(s).</b> As contagens acima são as de antes de
          aplicar — é o relatório da mesma planilha, para conferir o que entrou.
        </p>
      )}

      <label style={{
        display: "flex", alignItems: "center", gap: 6, fontSize: 11.5,
        color: "var(--txt2)", marginBottom: 8,
      }}>
        <input type="checkbox" checked={soAsQueMudam}
               onChange={(e) => setSoAsQueMudam(e.target.checked)} />
        Só as que mudam
      </label>

      <p style={{ margin: "0 0 6px", fontSize: 11, color: "var(--txt3)" }}>
        {linhas.length.toLocaleString("pt-BR")} linha(s)
        {linhas.length > mostradas.length && <> · mostrando {MOSTRADAS}</>}
      </p>

      <div style={{ overflow: "auto", border: "1px solid var(--line2)", borderRadius: 6 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11.5 }}>
          <thead>
            <tr>
              {["Linha", "Id", "Hoje", "Vai virar", "Situação"].map((h) => (
                <th key={h} style={cabeca}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {mostradas.map((l) => <Linha key={`${l.linha}-${l.id}`} l={l} />)}
          </tbody>
        </table>
      </div>
    </>
  )
}

function Linha({ l }: { l: LinhaDoLote }) {
  const muda = /vai mudar/i.test(l.situacao)
  const ruim = /outro projeto|não existe|nao existe|incompleta/i.test(l.situacao)

  return (
    <tr style={{ borderBottom: "1px solid var(--line2)" }}>
      <td style={{ ...celula, textAlign: "right", color: "var(--txt3)", width: 60 }}>{l.linha}</td>
      <td className="mono" style={{ ...celula, width: 110 }}>{l.id}</td>
      <td style={{ ...celula, color: "var(--txt2)" }}>{l.valorAtual || "—"}</td>
      <td style={{ ...celula, fontWeight: muda ? 600 : undefined }}>{l.valor || "—"}</td>
      <td style={{
        ...celula, width: 150,
        color: muda ? "var(--ok)" : ruim ? "var(--erro)" : "var(--txt3)",
      }}>
        {l.situacao}
      </td>
    </tr>
  )
}

function Numero({ rotulo, valor, cor }: { rotulo: string; valor: number; cor?: string }) {
  return (
    <div style={{ padding: "6px 11px", borderRadius: 6, border: "1px solid var(--line2)", minWidth: 96 }}>
      <div style={{ fontSize: 10, color: "var(--txt3)" }}>{rotulo}</div>
      <div style={{ fontSize: 16, fontWeight: 600, fontVariantNumeric: "tabular-nums", color: cor }}>
        {valor.toLocaleString("pt-BR")}
      </div>
    </div>
  )
}

// ------------------------------------------------------------------ apelidos

/**
 * Sugestões de apelido.
 *
 * **As duas listas não valem a mesma coisa, e a tela separa.** Por *código* são
 * apelidos de projetos **da mesma marca** — é o mesmo componente, com o mesmo
 * código, batizado por alguém que fez o mesmo trabalho. Por *descrição* são de
 * qualquer projeto: mais fartas, menos confiáveis.
 *
 * Não grava. Copiar o nome e usá-lo é a alteração do componente, que mora na aba
 * de cadastros — aqui é o lugar de descobrir o nome, não de aplicá-lo em massa.
 */
export function ApelidosSugeridos() {
  const { projetoId } = useParams()
  const projeto = Number(projetoId)

  const [somenteSemApelido, setSomenteSemApelido] = useState(true)
  const [busca, setBusca] = useState("")

  const consulta = usarSugestoesDeApelido(projeto, somenteSemApelido)
  const d = consulta.data

  const aplicar = usarAplicarApelidos(projeto)
  const [recado, setRecado] = useState<string | null>(null)

  function usarNome(componenteId: number, apelido: string) {
    setRecado(null)
    aplicar.mutate([{ componenteId, apelido }], {
      onSuccess: (r) => setRecado(r.alterados > 0
        ? `Apelido gravado: ${apelido}.`
        : "Este componente já estava com esse nome."),
      onError: (e) => setRecado(mensagem(e)),
    })
  }

  const itens = useMemo(() => {
    const t = busca.trim().toLowerCase()
    const todos = d?.itens ?? []
    if (!t) return todos
    return todos.filter((i) =>
      `${i.codigo} ${i.descricao} ${i.apelido}`.toLowerCase().includes(t))
  }, [d, busca])

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <div style={{
        display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center",
        padding: "12px 18px", borderBottom: "1px solid var(--line2)",
      }}>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "var(--txt2)" }}>
          <input type="checkbox" checked={somenteSemApelido}
                 onChange={(e) => setSomenteSemApelido(e.target.checked)} />
          Só os que ainda não têm apelido
        </label>
        <input value={busca} onChange={(e) => setBusca(e.target.value)}
               placeholder="filtrar por código ou descrição…"
               style={{ ...estiloCampo, width: 260 }} />
        {d && (
          <span style={{ fontSize: 11.5, color: "var(--txt2)" }}>
            {d.comSugestao} de {d.semApelido} sem apelido têm sugestão · {d.componentes} componentes no projeto
          </span>
        )}
      </div>

      <div style={{ flex: 1, minHeight: 0, overflow: "auto", padding: 14 }}>
        {consulta.isPending ? (
          <p style={{ fontSize: 12.5, color: "var(--txt3)" }}>Procurando nos outros projetos…</p>
        ) : consulta.error ? (
          <p style={{ fontSize: 12.5, color: "var(--erro)" }}>{mensagem(consulta.error)}</p>
        ) : itens.length === 0 ? (
          <p style={{ fontSize: 12.5, color: "var(--txt3)" }}>
            Nenhum componente no filtro.
          </p>
        ) : (
          itens.map((i) => (
            <div key={i.componenteId} style={{
              marginBottom: 6, padding: "8px 11px", borderRadius: 6,
              border: "1px solid var(--line2)",
            }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                <b className="mono" style={{ fontSize: 12 }}>{i.codigo || "—"}</b>
                <span style={{ fontSize: 11.5, color: "var(--txt2)" }}>{i.descricao || "sem descrição"}</span>
                {i.apelido && (
                  <span style={{ fontSize: 10.5, color: "var(--txt3)" }}>hoje: {i.apelido}</span>
                )}
              </div>

              {i.porCodigo.length === 0 && i.porDescricao.length === 0 ? (
                <p style={{ margin: "4px 0 0", fontSize: 11, color: "var(--txt3)" }}>
                  Nenhum outro projeto batizou este componente.
                </p>
              ) : (
                <>
                  {i.porCodigo.length > 0 && (
                    <Sugestoes
                      rotulo="Pelo código, na mesma marca"
                      nomes={i.porCodigo}
                      forte
                      aoUsar={(n) => usarNome(i.componenteId, n)}
                      ocupado={aplicar.isPending}
                    />
                  )}
                  {i.porDescricao.length > 0 && (
                    <Sugestoes
                      rotulo="Pela descrição, em qualquer projeto"
                      nomes={i.porDescricao}
                      aoUsar={(n) => usarNome(i.componenteId, n)}
                      ocupado={aplicar.isPending}
                    />
                  )}
                </>
              )}
            </div>
          ))
        )}
      </div>

      <BarraDeEstado direita={d ? `${d.componentes} componentes` : undefined}>
        {recado ?? "Clicar num nome grava o apelido no componente."}
      </BarraDeEstado>
    </div>
  )
}

function Sugestoes({ rotulo, nomes, forte, aoUsar, ocupado }: {
  rotulo: string
  nomes: string[]
  forte?: boolean
  aoUsar: (nome: string) => void
  ocupado?: boolean
}) {
  const [copiado, setCopiado] = useState("")

  return (
    <div style={{ marginTop: 5 }}>
      <div style={{ fontSize: 10, color: "var(--txt3)", marginBottom: 3 }}>{rotulo}</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
        {nomes.map((n) => (
          <button
            key={n}
            disabled={ocupado}
            onClick={() => { setCopiado(n); aoUsar(n) }}
            title="Gravar este apelido no componente"
            style={{
              padding: "2px 7px", fontSize: 11, borderRadius: 4, cursor: "pointer",
              border: "1px solid " + (forte ? "var(--acao)" : "var(--line)"),
              background: copiado === n ? "var(--surface2)" : "transparent",
              color: forte ? "var(--txt)" : "var(--txt2)",
            }}
          >
            {n}{copiado === n && " ✓"}
          </button>
        ))}
      </div>
    </div>
  )
}

const cabeca: React.CSSProperties = {
  position: "sticky", top: 0, background: "var(--surface)", zIndex: 1,
  textAlign: "left", padding: "5px 8px", fontSize: 10.5, fontWeight: 600,
  color: "var(--txt2)", borderBottom: "1px solid var(--line)", whiteSpace: "nowrap",
}

const celula: React.CSSProperties = { padding: "4px 8px" }
